import { useState, useEffect } from "react";
import useAudioRecorder from "@/hooks/use-audio-recorder";
import { getAudioType } from "@/utils/blob-utils";
import { useFFmpegContext } from "@/providers/ffmpeg-provider/ffmpeg-provider";
import { useMediaInputContext } from "@/providers/media-input-provider/media-input-provider";

const useRecordAudioData = () => {
  const {
    startRecording,
    stopRecording,
    isRecording,
    recordingTime,
    recordingBlob,
  } = useAudioRecorder();

  const [recordFile, setRecordFile] = useState<File | null>(null);
  const [recordFileName, setRecordFileName] = useState("");
  const [audioURL, setAudioURL] = useState("");
  const { ffmpeg, fetchFile, load } = useFFmpegContext();
  const { handleFileChange, uploadedFiles } = useMediaInputContext();
  const resetRecord = () => {
    setAudioURL("");
    handleFileChange({
      type: "recordAudio",
      songFile: undefined,
    });
  };

  useEffect(() => {
    setRecordFile(uploadedFiles.recordAudio?.songFile ?? null);
    setRecordFileName(uploadedFiles.recordAudio?.songFile?.name ?? "");
  }, [uploadedFiles.recordAudio]);

  useEffect(() => {
    const convertToMP3 = async () => {
      if (recordingBlob) {
        load();
        const audioFile = new File(
          [recordingBlob],
          `recorded-audio.${getAudioType(recordingBlob.type)}`,
          {
            type: recordingBlob.type,
          },
        );
        await ffmpeg?.writeFile(audioFile.name, await fetchFile(audioFile));
        await ffmpeg?.exec([
          "-i",
          audioFile.name,
          "-vn",
          "-acodec",
          "mp3",
          "-y",
          "output.mp3",
        ]);
        const data = await ffmpeg?.readFile("output.mp3");
        const mp3Blob = new Blob([data!], { type: "audio/mpeg" });
        const mp3Url = URL.createObjectURL(mp3Blob);

        setAudioURL(mp3Url);

        handleFileChange({
          type: "recordAudio",
          songFile: new File([mp3Blob], `recorded-audio.mp3`, {
            type: "audio/mp3",
          }),
        });
      }
    };
    convertToMP3();
  }, [recordingBlob]);

  return {
    isRecording,
    audioURL,
    startRecording,
    stopRecording,
    setAudioURL,
    resetRecord,
    recordFile,
    recordingTime,
  };
};

export default useRecordAudioData;
